import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const CURRENT_YEAR = new Date().getFullYear();
const digitsOnly = (v) => String(v).replace(/\D/g, '');

// 스마트폰에서 작성한 교수계획서는 임시용이라 수업구분을 '잠정'으로 저장한다 (PC에서 정규/보충/특별 등으로 수정).
// 수업 배정은 정규수업배정 기준으로 불러오고, 일일 수업시간은 4시간으로 고정한다.
const PLAN_TYPECLS = '잠정';
const ASSIGN_TYPECLS = '정규';
const DEFAULT_DAYTIME = 4;
const NOTICE_KEY = 'mobileLessonPlanNoticeClosed';

// 닫은 상태는 이 폰에만 저장한다 (저장소를 못 쓰는 환경이면 매번 안내를 보여준다)
const readNoticeClosed = () => {
  try {
    return localStorage.getItem(NOTICE_KEY) === '1';
  } catch (e) {
    return false;
  }
};

// 챕터 단위로 입력한 학과/실습 시수를, 선택한 세부교육내용들에 나눠 담는다 (서버는 섹션별 시수를 받는다).
// 학과를 앞 섹션부터 한 시간씩 채우고, 실습은 이어지는 섹션부터 채워서 시수가 한 곳에 몰리지 않게 한다.
function spreadHours(sections, thry, prac) {
  const n = sections.length;
  const rows = sections.map((s) => ({ crssectno: s.crssectno, thrytime: 0, practime: 0, remark: null }));
  for (let i = 0; i < thry; i += 1) rows[i % n].thrytime += 1;
  const offset = thry % n;
  for (let i = 0; i < prac; i += 1) rows[(offset + i) % n].practime += 1;
  return rows;
}

export default function MobileLessonPlans() {
  const { user } = useAuth();
  // 스마트폰에서는 본인 수업에 대한 작성만 지원한다 (행정직은 목록 조회만, 원외교사 대리작성은 PC에서)
  const canWritePlan = user?.role === 'teacher' || user?.role === 'admin' || user?.role === 'foreign';

  const year = CURRENT_YEAR;
  const [assignments, setAssignments] = useState([]);
  const [noticeClosed, setNoticeClosed] = useState(readNoticeClosed);
  const [showAll, setShowAll] = useState(false); // false: 배정된 과목만, true: 전체 과목
  const [catalog, setCatalog] = useState(null);
  const [sectionsByMat, setSectionsByMat] = useState({});
  const [subject, setSubject] = useState('');
  const [crsmatno, setCrsmatno] = useState('');
  const [checked, setChecked] = useState({});
  const [grade, setGrade] = useState('1');
  const [unitno, setUnitno] = useState('1');
  const [thry, setThry] = useState('');
  const [prac, setPrac] = useState('');
  const [entries, setEntries] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const closeNotice = () => {
    setNoticeClosed(true);
    try {
      localStorage.setItem(NOTICE_KEY, '1');
    } catch (e) {
      // 저장하지 못해도 이번 화면에서는 닫힌다
    }
  };

  const loadPlans = useCallback(async () => {
    // 관리자/행정직도 스마트폰에서는 로그인한 본인의 교수계획서만 보이게 teachid를 넘긴다
    // 수업구분(잠정/정규 등)과 상관없이 본인의 교수계획서를 모두 보여준다 (PC에서 정규로 바꿔도 목록에 남도록)
    const res = await api.get('/lesson-plans', { params: { year, teachid: user?.teachid } });
    setPlans(res.data);
  }, [year, user?.teachid]);

  useEffect(() => {
    loadPlans();
    if (!canWritePlan) return;
    api.get('/teacher/assignments/me', { params: { year, typecls: ASSIGN_TYPECLS } }).then((res) => setAssignments(res.data));
  }, [year, canWritePlan, loadPlans]);

  // "전체 과목"을 처음 체크할 때 표준교재 전체 목록을 불러온다
  useEffect(() => {
    if (!showAll || catalog) return;
    api.get('/materials/catalog').then((res) => setCatalog(res.data));
  }, [showAll, catalog]);

  // 선택 가능한 교육내용: 기본은 배정받은 챕터, 전체 과목이면 표준교재 전체(배정된 챕터는 배정된 섹션만 나오도록 asnid 유지)
  const options = useMemo(() => {
    if (!showAll) return assignments;
    const asnByMat = new Map(assignments.map((a) => [a.crsmatno, a.asnid]));
    return (catalog || []).map((m) => ({ ...m, asnid: asnByMat.get(m.crsmatno) }));
  }, [showAll, assignments, catalog]);

  const subjects = useMemo(() => Array.from(new Set(options.map((o) => o.subject).filter(Boolean))), [options]);
  const currentSubject = subjects.includes(subject) ? subject : subjects[0] || '';
  const subjectChapters = useMemo(() => options.filter((o) => o.subject === currentSubject), [options, currentSubject]);
  const currentAssignment = subjectChapters.find((o) => o.crsmatno === crsmatno) || subjectChapters[0];
  const currentMat = currentAssignment?.crsmatno;

  useEffect(() => {
    setChecked({});
  }, [currentMat]);

  useEffect(() => {
    if (!currentAssignment || sectionsByMat[currentAssignment.crsmatno]) return;
    const params = currentAssignment.asnid ? { asnid: currentAssignment.asnid } : {};
    api
      .get(`/materials/${currentAssignment.crsmatno}/sections`, { params })
      .then((res) => setSectionsByMat((prev) => ({ ...prev, [currentAssignment.crsmatno]: res.data })));
  }, [currentAssignment, sectionsByMat]);

  useEffect(() => {
    const maxUnit = plans.reduce((m, p) => Math.max(m, p.unitno || 0), 0);
    setUnitno(String(maxUnit + 1));
  }, [plans]);

  const sections = (currentMat && sectionsByMat[currentMat]) || [];
  const checkedSections = sections.filter((s) => checked[s.crssectno]);
  const allChecked = sections.length > 0 && checkedSections.length === sections.length;
  const inputThry = Number(thry) || 0;
  const inputPrac = Number(prac) || 0;

  const handleSubjectChange = (next) => {
    setSubject(next);
    setCrsmatno('');
  };

  const handleChapterChange = (next) => setCrsmatno(next);

  const toggleAll = () => {
    if (allChecked) {
      setChecked({});
    } else {
      const next = {};
      sections.forEach((s) => {
        next[s.crssectno] = true;
      });
      setChecked(next);
    }
  };

  // "선정": 지금 고른 교육내용(챕터)과 세부교육내용, 학과/실습 시수를 교수계획서 내용 목록에 담는다
  const handleSelect = () => {
    if (!currentAssignment) {
      setMessage('과목과 교육내용을 먼저 선택해 주세요.');
      return;
    }
    if (checkedSections.length === 0) {
      setMessage('세부교육내용을 하나 이상 선택해 주세요.');
      return;
    }
    if (inputThry + inputPrac <= 0) {
      setMessage('학과 또는 실습 시수를 입력해 주세요.');
      return;
    }
    setEntries((prev) => [
      ...prev.filter((e) => e.crsmatno !== currentAssignment.crsmatno),
      {
        crsmatno: currentAssignment.crsmatno,
        subject: currentAssignment.subject,
        chapter: currentAssignment.chapter,
        amtcode: currentAssignment.amtcode,
        sections: checkedSections,
        thry: inputThry,
        prac: inputPrac,
      },
    ]);
    setChecked({});
    setThry('');
    setPrac('');
    setMessage('');
  };

  const removeEntry = (entry) => {
    if (window.confirm(`"${entry.chapter}"을(를) 교수계획서 내용에서 뺄까요?`)) {
      setEntries((prev) => prev.filter((e) => e.crsmatno !== entry.crsmatno));
    }
  };

  const handleSave = async () => {
    if (entries.length === 0) {
      setMessage('선정한 교육내용이 없습니다. 교육내용을 고르고 "선정"을 눌러주세요.');
      return;
    }
    const gradeNum = Number(grade);
    const unitNum = Number(unitno);
    if (gradeNum < 1 || gradeNum > 3 || unitNum < 1) {
      setMessage('학년(1~3)과 차시(1 이상)를 확인해 주세요.');
      return;
    }
    const amtcode = entries[0].amtcode;
    if (!amtcode) {
      setMessage('지정과목 매핑이 없는 교육내용이라 저장할 수 없습니다. 관리자에게 문의해 주세요.');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      await api.post('/teacher/lesson-plans', {
        crsyear: year,
        grade: gradeNum,
        amtcode,
        unitno: unitNum,
        typecls: PLAN_TYPECLS,
        daytime: DEFAULT_DAYTIME,
        remark: null,
        sections: entries.flatMap((e) => spreadHours(e.sections, e.thry, e.prac)),
      });
      setMessage('교수계획서가 저장되었습니다.');
      setEntries([]);
      await loadPlans();
    } catch (err) {
      setMessage(err.response?.data?.detail || '저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const entrySubjects = Array.from(new Set(entries.map((e) => e.subject).filter(Boolean))).join(' / ');

  return (
    <div className="page mobile-lessonplan-page">
      {canWritePlan && (
        <>
          {!noticeClosed && (
            <div className="mob-lp-notice">
              <div className="mob-lp-notice-text">
                <strong>{PLAN_TYPECLS}</strong> 교수계획서 — 스마트폰에서 임시로 사용하는 계획서입니다.
                PC에서 정규·보충·특별 등으로 수정할 수 있고, <strong>{PLAN_TYPECLS}</strong> 계획서의 출결처리는 스마트폰에서만 가능합니다.
              </div>
              <button type="button" className="mob-lp-notice-close" aria-label="안내 닫기" onClick={closeNotice}>✕</button>
            </div>
          )}
          <div className="card">
            <div className="mob-lp-scope">
              <span className="mob-lp-scope-text">{showAll ? '전체 과목에서 선정' : '배정된 과목에서 선정'}</span>
              <label className="mob-lp-all">
                전체 과목
                <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              </label>
            </div>
            <div className="mob-lp-row">
              <span className="mob-lp-label">과목</span>
              <select className="mob-lp-select" value={currentSubject} onChange={(e) => handleSubjectChange(e.target.value)}>
                {subjects.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
                {subjects.length === 0 && <option value="">{showAll ? '불러오는 중' : '배정된 과목 없음'}</option>}
              </select>
              <select className="mob-lp-select mob-lp-select-wide" value={currentMat || ''} onChange={(e) => handleChapterChange(e.target.value)}>
                {subjectChapters.map((o) => (
                  <option key={o.crsmatno} value={o.crsmatno}>{o.chapter}</option>
                ))}
              </select>
            </div>
            <div className="mob-lp-row mob-lp-row-nums">
              <span className="mob-lp-label">학년</span>
              <input className="mob-lp-num" inputMode="numeric" value={grade} onChange={(e) => setGrade(digitsOnly(e.target.value))} />
              <span className="mob-lp-label">차시</span>
              <input className="mob-lp-num" inputMode="numeric" value={unitno} onChange={(e) => setUnitno(digitsOnly(e.target.value))} />
              <span className="mob-lp-label">학과</span>
              <input className="mob-lp-num" inputMode="numeric" value={thry} onChange={(e) => setThry(digitsOnly(e.target.value))} />
              <span className="mob-lp-label">실습</span>
              <input className="mob-lp-num" inputMode="numeric" value={prac} onChange={(e) => setPrac(digitsOnly(e.target.value))} />
              <button className="btn-primary btn-inline mob-lp-pick-btn" type="button" onClick={handleSelect}>선정</button>
            </div>
          </div>

          <div className="card">
            <div className="mob-lp-title-row">
              <h3>세부교육내용</h3>
              <label className="mob-lp-all">
                전체
                <input type="checkbox" checked={allChecked} onChange={toggleAll} disabled={sections.length === 0} />
              </label>
            </div>
            <div className="mob-lp-sections">
              {sections.map((s) => (
                <label key={s.crssectno} className="mob-lp-section">
                  <span>{s.sections}</span>
                  <input
                    type="checkbox"
                    checked={!!checked[s.crssectno]}
                    onChange={() => setChecked((prev) => ({ ...prev, [s.crssectno]: !prev[s.crssectno] }))}
                  />
                </label>
              ))}
              {sections.length === 0 && (
                <div className="mob-lp-empty">
                  {options.length === 0
                    ? (showAll ? '교육내용을 불러오는 중입니다.' : '배정받은 교육내용이 없습니다. 오른쪽 위 "전체 과목"을 체크하면 모든 과목에서 고를 수 있습니다.')
                    : '교육내용을 선택하면 세부교육내용이 나옵니다.'}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="mob-lp-title-row">
              <h3>교수계획서 내용</h3>
              <button className="btn-primary btn-inline mob-lp-save-btn" type="button" onClick={handleSave} disabled={loading}>
                {loading ? '저장 중' : '저장'}
              </button>
            </div>
            <table className="mob-lp-table">
              <thead>
                <tr>
                  <th>학년</th>
                  <th>차시</th>
                  <th>과목</th>
                  <th>교육내용</th>
                  <th>학과</th>
                  <th>실습</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={e.crsmatno} onClick={() => removeEntry(e)}>
                    <td>{i === 0 ? grade : ''}</td>
                    <td>{i === 0 ? unitno : ''}</td>
                    <td>{i === 0 ? entrySubjects : ''}</td>
                    <td>{e.chapter}</td>
                    <td>{e.thry}</td>
                    <td>{e.prac}</td>
                  </tr>
                ))}
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={6} className="mob-lp-empty">교육내용을 선정하면 여기에 담깁니다.</td>
                  </tr>
                )}
              </tbody>
            </table>
            {entries.length > 0 && <div className="mob-lp-hint">행을 누르면 목록에서 뺄 수 있습니다.</div>}
            {message && <div className="form-message">{message}</div>}
          </div>
        </>
      )}

      <div className="card">
        <h3 className="mob-lp-list-title">교육계획서 목록</h3>
        <div className="mob-lp-list-scroll">
        <table className="mob-lp-table">
          <thead>
            <tr>
              <th>학년</th>
              <th>차시</th>
              <th>과목</th>
              <th>교육내용</th>
              <th>학과</th>
              <th>실습</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.crsuid}>
                <td>{p.grade}</td>
                <td>
                  {p.unitno}
                  {p.typecls === PLAN_TYPECLS && <span className="mob-lp-temp">{PLAN_TYPECLS}</span>}
                </td>
                <td>{p.subject}</td>
                <td>{p.chapter}</td>
                <td>{p.thrytime}</td>
                <td>{p.practime}</td>
              </tr>
            ))}
            {plans.length === 0 && (
              <tr>
                <td colSpan={6} className="mob-lp-empty">등록된 교수계획서가 없습니다.</td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
